import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import SmartImage from '../components/SmartImage';
import { useAuth } from '../context/AuthContext';
import {
  Shield,
  TrendingUp,
  BarChart3,
  Award,
  Users,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  Phone,
  MessageCircle,
  Mail,
  Download,
  Eye,
  Loader2,
  Edit2,
  ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  shield: Shield,
  trending: TrendingUp,
  chart: BarChart3,
  award: Award,
  users: Users,
  check: CheckCircle,
};

const CATEGORY_FILTERS = ['All', 'Kids', 'Schools', 'Adults', 'Holiday'];

const ProgramsPage = () => {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const { isAuthenticated, isAdmin } = useAuth();
  const isAdminUser = isAuthenticated && isAdmin && isAdmin();

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/programs-pathways-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to load programs config:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const c = config || {};
  const heroTitle = c.hero_title || 'Programs & Pathways';
  const heroSubtitle = c.hero_subtitle || 'Find the right program for every age and skill level.';
  const trustChips = c.trust_chips || [];
  
  // Filter to only show PUBLISHED programs, sorted by order
  const allPublishedPathways = (c.pathways || [])
    .filter(p => p.status === 'published')
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  
  // Apply category filter
  const pathways = activeFilter === 'All'
    ? allPublishedPathways
    : allPublishedPathways.filter(p => (p.categories || []).includes(activeFilter));
  
  const ladderTitle = c.ladder_title || 'Your Swimming Journey';
  const ladderSubtitle = c.ladder_subtitle || 'Clear progression from first splash to confident swimmer';
  const ladderSteps = c.ladder_steps || [];
  const faqTitle = c.faq_title || 'Program FAQ';
  const faqSubtitle = c.faq_subtitle || 'Common questions about our programs';
  const faqs = c.faqs || [];
  const ctaTitle = c.cta_title || 'Ready to Start?';
  const ctaSubtitle = c.cta_subtitle || 'Book an assessment or register today';
  const ctaPhone = c.cta_phone || '+268 7600 1234';
  const ctaWhatsapp = c.cta_whatsapp || '+26876001234';
  const ctaEmail = c.cta_email || 'info@eswatiniseals.com';
  const ctaPrimaryText = c.cta_primary_text || 'Book Assessment';
  const ctaPrimaryLink = c.cta_primary_link || '/#contact';
  const ctaSecondaryText = c.cta_secondary_text || 'WhatsApp Us';

  return (
    <div className="min-h-screen bg-white" data-testid="programs-page">
      {/* Admin Edit Button */}
      {isAdminUser && (
        <Link
          to="/admin/programs-pathways"
          className="fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg shadow-lg hover:shadow-xl hover:border-primary transition-all text-sm font-medium text-slate-700 hover:text-primary"
          data-testid="edit-programs-btn"
        >
          <Edit2 className="w-4 h-4" />
          Edit Programs
        </Link>
      )}

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 bg-gradient-to-b from-primary/5 via-cyan-50/50 to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-primary mb-8 transition-colors"
          >
            <ChevronDown className="w-4 h-4 rotate-90" />
            Back to Home
          </Link>

          <div className="text-center max-w-3xl mx-auto">
            <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Find Your Path</Badge>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              {heroTitle}
            </h1>
            <p className="text-xl text-slate-600 mb-10">
              {heroSubtitle}
            </p>

            {/* Trust Chips */}
            {trustChips.length > 0 && (
              <div className="flex flex-wrap items-center justify-center gap-3">
                {trustChips.map((chip, idx) => {
                  const IconComponent = ICON_MAP[chip.icon] || Shield;
                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-100"
                    >
                      <IconComponent className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium text-slate-700">{chip.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pathways Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-200">Our Programs</Badge>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900">
              Choose Your Pathway
            </h2>
          </div>

          {/* Filter Chips */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-12" data-testid="program-filters">
            {CATEGORY_FILTERS.map(filter => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  activeFilter === filter
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`filter-${filter.toLowerCase()}`}
              >
                {filter}
              </button>
            ))}
          </div>

          {pathways.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pathways.map((pathway) => {
                const flyerUrl = getFileUrl(pathway.flyer_pdf_url);
                return (
                  <Card 
                    key={pathway.id} 
                    className="group border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col"
                  >
                    {/* Image */}
                    <div className="aspect-[16/10] relative overflow-hidden bg-gradient-to-br from-primary/20 to-cyan-500/20">
                      {pathway.image_url ? (
                        <SmartImage
                          src={getFileUrl(pathway.image_url)}
                          alt={pathway.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <Users className="w-16 h-16 text-primary/30" />
                        </div>
                      )}
                      {/* Outcome Badge */}
                      {pathway.outcome_badge && (
                        <div className="absolute top-3 right-3">
                          <Badge className="bg-white/90 text-primary border-0 shadow-sm">
                            <Award className="w-3 h-3 mr-1" />
                            {pathway.outcome_badge}
                          </Badge>
                        </div>
                      )}
                    </div>

                    <CardContent className="p-6 flex-1 flex flex-col">
                      <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">
                        {pathway.title}
                      </h3>
                      <p className="text-sm text-primary font-medium mb-1">
                        {pathway.best_for}
                      </p>
                      {pathway.duration_text && (
                        <p className="text-xs text-slate-500 mb-3">
                          {pathway.duration_text}
                        </p>
                      )}
                      <p className="text-slate-600 text-sm mb-4">
                        {pathway.tagline}
                      </p>

                      {/* Bullets */}
                      {pathway.bullets && pathway.bullets.length > 0 && (
                        <div className="space-y-2 mb-6 flex-1">
                          {pathway.bullets.map((bullet, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                              <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              <span>{bullet}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* CTAs */}
                      <div className="flex flex-col gap-2 mt-auto">
                        <Link to={pathway.primary_cta_link || '/#contact'}>
                          <Button className="w-full bg-primary hover:bg-primary/90">
                            {pathway.primary_cta_text || 'Enquire Now'}
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                        {flyerUrl ? (
                          <div className="flex gap-2">
                            <a
                              href={flyerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:border-primary hover:text-primary transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              View Flyer
                            </a>
                            <a
                              href={flyerUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={`${pathway.title.replace(/\s+/g, '_')}_Flyer.pdf`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center justify-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded-lg hover:border-primary hover:text-primary transition-colors"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1 px-3 py-2 text-sm text-slate-400 border border-slate-100 rounded-lg bg-slate-50 cursor-not-allowed">
                            <Download className="w-4 h-4" />
                            Flyer coming soon
                          </div>
                        )}
                      </div>
                </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-programs-message">
              <Users className="w-16 h-16 mx-auto text-slate-200 mb-4" />
              <p className="text-slate-500 text-lg">No programs found for "{activeFilter}"</p>
              <button
                onClick={() => setActiveFilter('All')}
                className="mt-4 text-primary hover:underline text-sm"
              >
                Show all programs
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Progression Ladder */}
      {ladderSteps.length > 0 && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">Your Journey</Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                {ladderTitle}
              </h2>
              <p className="text-lg text-slate-600">{ladderSubtitle}</p>
            </div>

            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-cyan-400 to-emerald-400 transform md:-translate-x-1/2" />
              
              <div className="space-y-8">
                {ladderSteps.map((step, idx) => (
                  <div 
                    key={step.id || idx}
                    className={`relative flex items-center gap-6 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Step number */}
                    <div className="absolute left-0 md:left-1/2 w-12 h-12 rounded-full bg-white border-4 border-primary shadow-lg flex items-center justify-center transform md:-translate-x-1/2 z-10">
                      <span className="text-primary font-bold">{idx + 1}</span>
                    </div>
                    
                    {/* Content */}
                    <div className={`ml-20 md:ml-0 md:w-5/12 ${idx % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <div className="bg-white rounded-xl p-5 shadow-md border border-slate-100">
                        <h3 className="font-semibold text-slate-900 mb-1">{step.label}</h3>
                        <p className="text-sm text-slate-600">{step.description}</p>
                      </div>
                    </div>
                    
                    {/* Spacer for alternating layout */}
                    <div className="hidden md:block md:w-5/12" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">FAQ</Badge>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                {faqTitle}
              </h2>
              <p className="text-lg text-slate-600">{faqSubtitle}</p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, idx) => (
                <AccordionItem
                  key={faq.id || idx}
                  value={faq.id || `faq-${idx}`}
                  className="bg-slate-50 rounded-xl border-0 px-6"
                >
                  <AccordionTrigger className="text-left font-semibold text-slate-900 hover:no-underline py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/5 via-cyan-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-4 bg-white text-primary border-primary/20 shadow-sm">Get Started</Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {ctaTitle}
          </h2>
          <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto">
            {ctaSubtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to={ctaPrimaryLink}>
              <Button
                size="lg"
                className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-semibold px-10 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all group"
              >
                {ctaPrimaryText}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <a href={`https://wa.me/${ctaWhatsapp}`} target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold px-10 py-6 text-lg rounded-xl transition-all"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                {ctaSecondaryText}
              </Button>
            </a>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-slate-600">
            <a href={`tel:${ctaPhone.replace(/\s/g, '')}`} className="flex items-center gap-2 hover:text-primary transition-colors">
              <Phone className="w-4 h-4" />
              <span>{ctaPhone}</span>
            </a>
            <a href={`mailto:${ctaEmail}`} className="flex items-center gap-2 hover:text-primary transition-colors">
              <Mail className="w-4 h-4" />
              <span>{ctaEmail}</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 text-center">
        <div className="max-w-7xl mx-auto px-4">
          <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">
            ← Back to Eswatini Seals Swim Academy
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default ProgramsPage;
